'use client';

import React, { use, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { Typography } from 'lighthouse';

import { BatteryChoiceCard } from '@/widgets/BatteryChoiceCard';
import { BatteryChoiceCardLoading } from '@/widgets/BatteryChoiceCard/BatteryChoiceCardLoading';
import { InfiniteScrollListRef } from '@/widgets/InfiniteScrollList/models';
import LoginModal from '@/widgets/LoginModal';
import { PriceBasisNote } from '@/widgets/PriceBasisNote';
import { SelectableProductList } from '@/widgets/SelectableProductList';

import { getBatteryCompatibilityBadge } from '@/utilities/batteryProduct';
import { isUserLogin } from '@/utilities/CheckUserIsLogin';
import {
    mapBatteryRouteToDataLayerVehicle,
    setDataLayerContext,
    splitVehicleName,
    trackBatteryIdentityCheck,
    trackBatteryOtpSubFunnel,
    trackBatterySelection,
    trackBatteryVehicleSelection
} from '@/utilities/dataLayer';

import { useCityStore } from '@/stores/city/useCityStore';

import { isSuggestedBattery, vehicleApiTypeId, vehicleBatteryPageTitle, vehicleTypeTitle } from '@/constants/batteryProduct';
import { BATTERY_REQUEST_STEP, batteryQuickKeywordsHint, batteryQuickVehicleKeywordsByType, batteryRequestSteps } from '@/constants/batteryRequest';
import { PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID, vehicleTypesCategoryId } from '@/constants/common';
import { AUTH_METHODS } from '@/constants/dataLayer';
import { batteryRequest, requestReservation } from '@/constants/routes';

import RequestWrapper from '../../_components/RequestWrapper';
import { VehicleModelSelect } from '../../_components/VehicleModelSelect';
import { VehicleBatteryRequestType } from './models';

import { setBatteryOrderService } from '@/client/services/core/batteryOrder/methods';
import { getCategoryBySlugService } from '@/client/services/core/category/methods';
import { getProductBatteryByUuidService, getProductsBatteryByVehicleFiltersService } from '@/client/services/core/productBattery/methods';
import { getVehicleBySlug, getVehiclesService } from '@/client/services/core/vehicle/methods';
import { Vehicle } from '@/client/services/core/vehicle/models';
import { CategoryType } from '@/server/services/core/category/models';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';
import axios from 'axios';
import { cornbar } from 'cornbar';

const Page = (props: { params: Promise<{ vehicleType: VehicleBatteryRequestType }> }) => {
    const params = use(props.params);
    const infiniteScrollRef = useRef<InfiniteScrollListRef>(null);
    const autoAdvancedProductRef = useRef<string | null>(null);
    const [isAutoAdvanceDismissed, setIsAutoAdvanceDismissed] = useState(false);
    const searchParams = useSearchParams();
    const router = useRouter();
    const cityId = useCityStore((state) => state.cityId);

    const productId = searchParams.get('productId');
    const vehicleSlug = searchParams.get('vehicle');
    const { vehicleType } = params;
    const dataLayerVehicle = mapBatteryRouteToDataLayerVehicle(vehicleType);

    const [submitVehicleModelPending, setSubmitVehicleModelPending] = useState<boolean>(false);
    const [selectedProductBattery, setSelectedProductBattery] = useState<ProductBatteryType | null>(null);
    const [category, setCategory] = useState<CategoryType | null>(null);
    const [products, setProducts] = useState<ProductBatteryType[]>([]);
    const [isProductListLoading, setIsProductLoading] = useState(true);
    const [isSelectedProductListLoading, setIsSelectedProductLoading] = useState(true);
    const [selectedProductUuid, setSelectedProductUuid] = useState('');
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [vehicleModel, setVehicleModel] = useState<Vehicle | null>(null);

    const getVehicleBrandModel = () => splitVehicleName(vehicleModel?.name || '');

    const resolveSelectedProduct = (uuid: string): ProductBatteryType | null => {
        if (selectedProductBattery?.uuid === uuid) {
            return selectedProductBattery;
        }

        return products.find((product) => product.uuid === uuid) || null;
    };

    const trackBatterySelectionAndContext = (product: ProductBatteryType) => {
        const { brand, model } = getVehicleBrandModel();

        trackBatterySelection({
            vehicle: dataLayerVehicle,
            brand,
            model,
            itemId: product.uuid,
            itemName: product.title,
            batteryBrand: product.brand.name,
            price: product.sellingPrice
        });
        setDataLayerContext({
            batteryVehicle: dataLayerVehicle,
            batteryBrand: brand,
            batteryModel: model,
            batteryItemId: product.uuid,
            batteryItemName: product.title,
            batteryItemBrand: product.brand.name,
            batteryItemPrice: product.sellingPrice
        });
    };

    const isBatteryCompatible = (product: ProductBatteryType) => {
        const compatibility = vehicleModel?.params?.batteryCompatibility;

        if (!compatibility) return true;

        const isAmpereCompatible = compatibility.amperes?.includes(product.ampere);
        const isPoleCompatible = compatibility.pole ? compatibility.pole === product.pole : true;
        const isHeightCompatible = compatibility.height ? compatibility.height === product.height : true;

        return isAmpereCompatible && isPoleCompatible && isHeightCompatible;
    };

    useEffect(() => {
        if (!vehicleSlug) return;

        (async () => {
            try {
                const response = await getVehicleBySlug(vehicleSlug);
                const content = response.data.content;

                if (content) {
                    setVehicleModel(content);
                    const { brand, model } = splitVehicleName(content.name);
                    trackBatteryVehicleSelection({
                        vehicle: dataLayerVehicle,
                        brand,
                        model
                    });
                    setDataLayerContext({
                        batteryVehicle: dataLayerVehicle,
                        batteryBrand: brand,
                        batteryModel: model
                    });
                }
            } catch {}
        })();
    }, [vehicleSlug]);

    const handleGetCategoryWithProducts = async () => {
        try {
            const categoryResponse = await getCategoryBySlugService(vehicleType);
            if (categoryResponse.data.content) {
                setCategory(categoryResponse.data.content);
            } else {
                setIsSelectedProductLoading(false);
            }
        } catch {
        } finally {
            setIsSelectedProductLoading(false);
        }
    };

    useEffect(() => {
        void handleGetCategoryWithProducts();
    }, []);

    const handleGetProducts = async (page: number) => {
        const vehicleFilters = vehicleModel ? vehicleModel.params?.batteryCompatibility : undefined;
        try {
            const productResponse = await getProductsBatteryByVehicleFiltersService({
                poles: vehicleFilters?.pole ? [vehicleFilters.pole] : undefined,
                heights: vehicleFilters?.height ? [vehicleFilters.height] : undefined,
                amperes: vehicleFilters?.amperes,
                categoryIds: category?.id ? [category?.id] : [0],
                page,
                cityId,
                size: 10,
                sortOption: PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID,
                sort: ['ampere,asc']
            });
            return productResponse?.data?.content || [];
        } catch {
            return [];
        }
    };

    useEffect(() => {
        let isActive = true;
        (async () => {
            if (vehicleModel && category) {
                setIsSelectedProductLoading(true);
                const newProducts = await handleGetProducts(0);
                if (!isActive) return;
                setProducts(newProducts);
                infiniteScrollRef.current?.resetPaginate();
                setIsSelectedProductLoading(false);
            }
        })();
        return () => {
            isActive = false;
        };
    }, [vehicleModel, category, cityId]);

    useEffect(() => {
        if (!productId || !cityId || !vehicleModel) return;

        let isActive = true;
        setIsProductLoading(true);
        setSelectedProductBattery(null);
        void getProductBatteryByUuidService(productId, { cityId })
            .then((response) => {
                if (isActive) setSelectedProductBattery(response.data.content);
            })
            .catch(() => undefined)
            .finally(() => {
                if (isActive) setIsProductLoading(false);
            });

        return () => {
            isActive = false;
        };
    }, [productId, cityId, vehicleModel]);

    const handleSearchVehicleBrands = async (input: string) => {
        try {
            const vehiclesResponse = await getVehiclesService({
                serviceType: 'BATTERY',
                keyword: input,
                vehicleType: vehicleApiTypeId[vehicleType],
                page: 0,
                size: 30,
                sort: 'priority,desc'
            });
            return vehiclesResponse.data.content;
        } catch {}
        return [];
    };

    const handleSubmitVehicleModel = async (value: Vehicle) => {
        try {
            setSubmitVehicleModelPending(true);
            setVehicleModel(value);
            const { brand, model } = splitVehicleName(value.name);
            trackBatteryVehicleSelection({
                vehicle: dataLayerVehicle,
                brand,
                model
            });
            setDataLayerContext({
                batteryVehicle: dataLayerVehicle,
                batteryBrand: brand,
                batteryModel: model
            });
        } catch {
        } finally {
            setSubmitVehicleModelPending(false);
        }
    };

    const getCompatibilityBadge = (product: ProductBatteryType) =>
        getBatteryCompatibilityBadge(vehicleModel?.params?.batteryCompatibility?.amperes?.[0] === product?.ampere, vehicleTypeTitle[vehicleType]);

    const handleSelectProduct = (uuid: string) => {
        setSelectedProductUuid(uuid);
        void handleNextStep(uuid);
    };

    const handleNextStep = async (uuid: string, options?: { fromLogin?: boolean }) => {
        setIsSubmitLoading(true);
        const product = resolveSelectedProduct(uuid);
        const fromLogin = Boolean(options?.fromLogin);
        const authMethod = fromLogin ? AUTH_METHODS.OTP_VERIFIED : AUTH_METHODS.ALREADY_LOGGED_IN;

        if (!isUserLogin()) {
            if (product) {
                trackBatterySelectionAndContext(product);
            }
            setIsAutoAdvanceDismissed(true);
            setShowLoginModal(true);
            setIsSubmitLoading(false);
            return;
        }

        try {
            if (!fromLogin && product) {
                trackBatterySelectionAndContext(product);
            }

            const { brand, model } = getVehicleBrandModel();
            trackBatteryIdentityCheck({
                vehicle: dataLayerVehicle,
                brand,
                model,
                authMethod
            });

            await setBatteryOrderService({ productUuid: uuid, vehicleId: vehicleModel!.id!, cityId }).then((res) => {
                if (res.status === 201) {
                    if (autoAdvancedProductRef.current === uuid) {
                        const url = new URL(window.location.href);
                        url.searchParams.delete('productId');
                        window.history.replaceState(window.history.state, '', url);
                    }
                    void router.push(`/${vehicleType}${batteryRequest}/${res.data.content.uuid}${requestReservation}`);
                } else {
                    setIsAutoAdvanceDismissed(true);
                    cornbar.show({
                        title: 'خطا رخ داد',
                        description: 'درخواست شما ثبت نشد. لطفا مجدد تلاش کنید!',
                        variant: 'error'
                    });
                }
            });
        } catch (error) {
            setIsAutoAdvanceDismissed(true);
            setIsSubmitLoading(false);
            if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
                setShowLoginModal(true);
            }
        }
    };

    const isAutoAdvancing =
        !isAutoAdvanceDismissed &&
        !!productId &&
        !!vehicleModel?.params?.batteryCompatibility &&
        selectedProductBattery?.uuid === productId &&
        selectedProductBattery.available &&
        isBatteryCompatible(selectedProductBattery);

    useEffect(() => {
        if (!isAutoAdvancing || !productId || autoAdvancedProductRef.current === productId) return;

        autoAdvancedProductRef.current = productId;
        handleSelectProduct(productId);
    }, [isAutoAdvancing, productId]);

    const handleUserLogin = () => {
        setShowLoginModal(false);
        if (selectedProductUuid) {
            if (autoAdvancedProductRef.current === selectedProductUuid) setIsAutoAdvanceDismissed(false);
            void handleNextStep(selectedProductUuid, { fromLogin: true });
        }
    };

    const handleCloseLoginModal = () => {
        setShowLoginModal(false);
        setIsSubmitLoading(false);
    };

    const loginAnalytics = {
        onPopupShown: () => {
            const { brand, model } = getVehicleBrandModel();
            trackBatteryOtpSubFunnel({ vehicle: dataLayerVehicle, step: 'popup_shown', brand, model });
        },
        onOtpCodeRequested: () => {
            const { brand, model } = getVehicleBrandModel();
            trackBatteryOtpSubFunnel({ vehicle: dataLayerVehicle, step: 'code_requested', brand, model });
        },
        onOtpVerified: () => {
            const { brand, model } = getVehicleBrandModel();
            trackBatteryOtpSubFunnel({ vehicle: dataLayerVehicle, step: 'verified', brand, model });
        }
    };

    return (
        <RequestWrapper
            steps={batteryRequestSteps}
            currentStep={BATTERY_REQUEST_STEP.select}
            headerTitle={vehicleBatteryPageTitle[vehicleType].headerTitle}
            title={vehicleBatteryPageTitle[vehicleType].title}>
            <div className='mb-5 mt-6' style={{ containerType: 'inline-size' }}>
                <VehicleModelSelect
                    onSearchVehicleBrands={handleSearchVehicleBrands}
                    onSubmitVehicleModel={handleSubmitVehicleModel}
                    isSubmitLoading={submitVehicleModelPending}
                    title={vehicleBatteryPageTitle[vehicleType].inputPlaceholder}
                    value={vehicleModel}
                    autoFocus={!vehicleSlug}
                    quickKeywords={batteryQuickVehicleKeywordsByType[vehicleType]}
                    quickKeywordsHint={batteryQuickKeywordsHint[vehicleType]}
                />
            </div>
            {vehicleModel && (
                <>
                    <PriceBasisNote className='my-3' isExpandable={false} />
                    {productId &&
                        (isProductListLoading || isAutoAdvancing ? (
                            <>
                                <BatteryChoiceCardLoading count={1} />
                                {isAutoAdvancing && (
                                    <Typography variant='caption' color='blackMediumEmphasis' display='block' className='pt-3 text-center'>
                                        سازگار است؛ در حال انتقال به مرحله‌ی بعد...
                                    </Typography>
                                )}
                            </>
                        ) : (
                            selectedProductBattery && (
                                <>
                                    <Typography variant='caption' color='blackMediumEmphasis' display='block' className='pb-3'>
                                        باتری انتخاب شده
                                    </Typography>
                                    <BatteryChoiceCard
                                        product={selectedProductBattery}
                                        className='mb-3 w-full min-w-full'
                                        badge={
                                            selectedProductBattery.available
                                                ? isBatteryCompatible(selectedProductBattery)
                                                    ? getCompatibilityBadge(selectedProductBattery)
                                                    : { label: `این باتری مناسب ${vehicleTypeTitle[vehicleType]} شما نیست`, tone: 'error' }
                                                : undefined
                                        }
                                        disabled={!isBatteryCompatible(selectedProductBattery)}
                                        isSuggested={!!isSuggestedBattery(selectedProductBattery)}
                                        isLoading={isSubmitLoading}
                                        buttonTitle='تایید و ادامه'
                                        onSelect={handleSelectProduct}
                                    />
                                </>
                            )
                        ))}

                    {isAutoAdvancing ? null : isSelectedProductListLoading ? (
                        <div className='flex flex-col gap-4 px-2 pt-3 [@container_(min-width:768px)]:grid [@container_(min-width:768px)]:grid-cols-2'>
                            <BatteryChoiceCardLoading />
                        </div>
                    ) : (
                        !!products.length && (
                            <>
                                {!!productId && (
                                    <Typography variant='caption' color='blackMediumEmphasis' display='block' className='pb-3 pt-4'>
                                        {`دیگر باتری‌های مناسب برای ${vehicleModel.name}`}
                                    </Typography>
                                )}
                                <SelectableProductList
                                    key={cityId}
                                    vehicleModel={vehicleModel}
                                    vehicleType={vehicleType}
                                    filterOptions={{
                                        categoryIds: [vehicleTypesCategoryId[vehicleModel?.vehicleType as keyof typeof vehicleTypesCategoryId]],
                                        ...(vehicleModel.params?.batteryCompatibility
                                            ? {
                                                  amperes: vehicleModel.params.batteryCompatibility?.amperes,
                                                  poles: vehicleModel.params.batteryCompatibility?.pole,
                                                  heights: vehicleModel.params.batteryCompatibility?.height
                                              }
                                            : {})
                                    }}
                                />
                            </>
                        )
                    )}
                </>
            )}

            {showLoginModal && <LoginModal onUserLogin={handleUserLogin} onClose={handleCloseLoginModal} analytics={loginAnalytics} />}
        </RequestWrapper>
    );
};

export default Page;
